import React, {useCallback, useEffect, useMemo, useState} from "react";
import {DialogContent, DialogTitle, Divider} from "@mui/material";
import Dialog from "../../base/dialog";
import LoadingLottieAnimation from "../../app-specific/lottie-animations/loading";
import ChargeCreditCardForm from "./sections/charge-credit-card-form";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import TransactionReceiptInformation from "../../app-specific/transaction-receipt-information";
import useAccessLevels from "../../../hooks/use-access-levels";
import ToastService from "../../../../core/services/toast";

/**
 * Different steps for charging a credit card
 */
export const ChargeCreditCardSteps = {
    Form: 'form',
    Loading: 'loading',
    Receipt: 'receipt',
}

const ChargeCreditCardDialog = ({
                                    card,
                                    open,
                                    setOpen,
                                    customerId,
                                }) => {
    const [step, setStep] = useState(ChargeCreditCardSteps.Form);
    const [receiptId, setReceiptId] = useState(null);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();

    /**
     * Warns the user when the main API is used in the development environment.
     */
    useEffect(() => {
        if (open) {
            ToastService.mainAPIRedZoneWarning();
        }
    }, [open])

    /**
     * With each change in the open state of the dialog:
     * - changes the step of the component to [form].
     */
    useEffect(() => {
        setStep(ChargeCreditCardSteps.Form);
        setReceiptId(null);
    }, [open])

    /**
     * Charges the credit card:
     * - Sets the [step] in the state to loading
     * - Calls the api for charging the credit card
     * - Based on the result of api, shows the receipt of the payment by setting the [step] as receipt
     */
    const onCharge = useCallback(async (forApi) => {
        if (!operatorAccessLevels.packages.sales.chargeCreditCards) return;
        setStep(ChargeCreditCardSteps.Loading);
        const response = await Api.chargeCustomerCreditCard(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            if (operatorAccessLevels.packages.sales.paymentReceipts) {
                setReceiptId(response?.data?.receiptId);
                setStep(ChargeCreditCardSteps.Receipt);
            } else {
                setOpen(false);
            }
        } else {
            setStep(ChargeCreditCardSteps.Form);
        }
    }, [isMounted, operatorAccessLevels.packages.sales.chargeCreditCards, operatorAccessLevels.packages.sales.paymentReceipts, setOpen]);

    /**
     * Memo version of dialog size
     * @type {string}
     */
    const dialogSize = useMemo(() => {
        switch (step) {
            case ChargeCreditCardSteps.Receipt:
                return 'md';
            case ChargeCreditCardSteps.Loading:
            default:
                return 'sm';
            case ChargeCreditCardSteps.Form:
                return 'md';
        }
    }, [step])

    /**
     * Closes the dialog
     * @type {function(): *}
     */
    const close = useCallback(() => setOpen(false), [setOpen]);

    return (
        <Dialog
            open={open}
            setOpen={setOpen}
            maxWidth={dialogSize}
            className={'bound-dialog charge-credit-card-dialog'}
            scroll={'paper'}
            aria-labelledby="scroll-dialog-title"
        >
            <DialogTitle id={'scroll-dialog-title'}>
                <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                    Charge Credit Card
                </p>
                <Divider className='divider'/>
            </DialogTitle>
            {
                ChargeCreditCardSteps.Form === step &&
                <ChargeCreditCardForm
                    card={card}
                    customerId={customerId}
                    onCharge={onCharge}
                    close={close}
                />
            }
            {
                ChargeCreditCardSteps.Loading === step &&
                <DialogContent className={'my-3'}>
                    <LoadingLottieAnimation width={'90%'}/>
                </DialogContent>
            }
            {
                ChargeCreditCardSteps.Receipt === step &&
                <TransactionReceiptInformation
                    done={close}
                    customerId={customerId}
                    receiptId={receiptId}
                />
            }
        </Dialog>
    );
}

export default ChargeCreditCardDialog;
